import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TapHand } from "@/shared/components/TapHand";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";

const K = keys.coachmark;

export type TourStep = {
  /**
   * Value of the `data-tour` attribute on the element to spotlight, or null for
   * a step that explains the SCREEN rather than a control on it — the card is
   * centred over a plain dim, with no cut-out. Page help opens on one of these:
   * "here is what this page is for", before it points at anything.
   */
  anchor: string | null;
  title: string;
  body: string;
  /**
   * This anchor DOES NOT EXIST when the tour opens — it is created before the
   * step is reached, by the seller's click on the previous `awaitClick` step or
   * by a page watching `useHelpStep`. Such a step must not be filtered out for
   * being absent while it is still waiting to appear.
   */
  provided?: boolean;
  /**
   * Advance when the seller CLICKS the anchor, not when they press Next. The
   * step shows a tapping hand over it and drops its Next button — being told
   * "open a customer" and then pressing "Next" teaches the button, not the
   * task. The click does the real thing, which is what opens the surface the
   * following step explains.
   */
  awaitClick?: boolean;
};

type Rect = { top: number; left: number; width: number; height: number };

const PAD = 8;
const GAP = 12;
// 300 was tuned for a one-line hint beside a field. Page help carries a
// paragraph explaining a whole screen, which wrapped to five or six short
// lines in that width and read as a narrow column of text. Still clamped to
// `100vw - 24px`, so a 375px phone is unaffected.
const CARD_W = 372;
/** Where a screen-level card sits: clear of the notch, clear of the content. */
const SCREEN_CARD_TOP = "calc(env(safe-area-inset-top, 0px) + 16px)";

function readRect(anchor: string): Rect | null {
  const el = document.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // A zero-size or off-screen anchor is as good as absent — a collapsed field
  // would otherwise get a spotlight over nothing.
  if (r.width === 0 || r.height === 0) return null;
  // CLAMPED to the viewport. A scrollable list is taller than the screen, and
  // an unclamped rect both draws a cut-out running off the bottom and makes the
  // "does this fill the viewport?" test below answer yes for anything long —
  // which turned a highlighted list into a flat dim. You cannot spotlight what
  // is not on screen; highlight the part that is.
  const top = Math.max(0, r.top);
  const left = Math.max(0, r.left);
  const bottom = Math.min(window.innerHeight, r.bottom);
  const right = Math.min(window.innerWidth, r.right);
  if (bottom <= top || right <= left) return null;
  return { top, left, width: right - left, height: bottom - top };
}

/**
 * The first row/card INSIDE an anchor, when the page marks one.
 *
 * An `awaitClick` step says "open a customer" — pointing the hand at the
 * middle of a scrolling list would be pointing at nothing in particular, and
 * accepting a click anywhere in the list would let a tap on the padding
 * advance to a step explaining a profile that never opened. `data-tour-item`
 * lets the page nominate the things that are actually clickable.
 */
function readItemRect(anchor: string): Rect | null {
  const el = document.querySelector<HTMLElement>(
    `[data-tour="${anchor}"] [data-tour-item]`,
  );
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null;
  const top = Math.max(0, r.top);
  const bottom = Math.min(window.innerHeight, r.bottom);
  if (bottom <= top) return null;
  return { top, left: r.left, width: r.width, height: bottom - top };
}

/**
 * Spotlight walkthrough for a single surface (the create-category modal, the
 * create-product drawer…).
 *
 * Anchors are explicit `data-tour` attributes, NOT CSS selectors over markup
 * structure: a tour that reaches into class names or DOM shape silently breaks
 * the next time the form is restyled, and nobody notices until a merchant hits
 * it. An attribute travels with the element it names, and its absence is
 * greppable.
 *
 * Every miss FAILS SOFT — a step whose anchor is gone is skipped, and a tour
 * with no resolvable steps left closes itself. A broken walkthrough must never
 * block the form it is explaining.
 */
export function Coachmark({
  steps,
  onClose,
  onProceed,
  proceedLabel,
  sampleNotice,
  onStepChange,
  spotlightClickable = false,
}: {
  steps: TourStep[];
  onClose: () => void;
  /**
   * The seller ACCEPTED the tour rather than dismissing it — they pressed the
   * spotlit control, or the primary button on the last step. "Skip", Escape and
   * a click on the scrim are refusals and stay on `onClose`. Defaults to
   * `onClose`, so a caller that does not care sees no difference.
   */
  onProceed?: () => void;
  /**
   * Names the last step's primary button when `onProceed` does something
   * other than close — "Show me", "Try it on a real order". Without it the
   * button says "Done" and then does something, which is a lie about what
   * pressing it will do.
   */
  proceedLabel?: string;
  /**
   * Shown under the body while the page beneath is standing in sample rows for
   * an empty list — "this is sample data, it goes when help closes".
   */
  sampleNotice?: string;
  /** Fires with the anchor of each step as it becomes active. */
  onStepChange?: (anchor: string | null) => void;
  /**
   * Let clicks through to the spotlit element instead of swallowing them.
   * Used by the "press this button" hint, where the whole point is that the
   * seller performs the action themselves — a tour that highlights a button
   * and then eats the click is worse than no tour.
   */
  spotlightClickable?: boolean;
}) {
  const t = useTranslations();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  /** False until the anchor has settled and been scrolled to (see below). */
  const [ready, setReady] = useState(false);
  /**
   * Has anything been shown yet? Only the FIRST step may render nothing while
   * it settles. On every step after it the overlay STAYS UP — hiding it while
   * the next anchor is measured made the whole tour vanish for a beat when
   * "Next" was pressed, which reads exactly like a dead button. The card swaps
   * to the new step immediately and the spotlight catches up when it lands.
   */
  const revealedOnce = useRef(false);

  const step: TourStep | undefined = steps[index];

  // Resolve the anchor, skipping any that are not on screen. Re-measured on
  // scroll/resize because the modal it sits in is itself scrollable.
  // Keyed on the anchor STRING, not the step object: callers build their step
  // array inline, so a step object has a new identity every render — depending
  // on it re-ran this effect and re-set state on every commit, which thrashed.
  const anchor = step?.anchor;
  const stepAnchor = anchor ?? null;
  // A step that waits for a real click has to let it through, whatever the
  // caller asked for.
  const clickThrough = spotlightClickable || step?.awaitClick === true;
  useEffect(() => {
    onStepChange?.(stepAnchor);
  }, [stepAnchor, onStepChange]);

  /**
   * Settle → scroll → reveal, in that order, and show nothing until all three
   * have happened.
   *
   * The spotlight used to paint the moment it could measure ANYTHING, which put
   * it in a race with the page it was pointing at: a panel still mounting, a
   * section still fetching, a page transition still running. The seller saw the
   * highlight land on the wrong box and then jump, or land on a box that had
   * not finished growing. So:
   *
   *  1. Poll until the anchor measures non-zero AND reports the SAME rect twice
   *     in a row — one stable reading is what "the page has rendered" means
   *     here, and it costs one extra 60ms tick.
   *  2. Scroll it into view if it is off-screen, and wait for that to finish
   *     before measuring again — measuring mid-scroll is how a spotlight ends
   *     up offset by exactly the scroll distance.
   *  3. Only then reveal, with a fade (see `.coachmark-in`).
   *
   * After the reveal the poll keeps running WITHOUT the stability gate, so the
   * spotlight still follows a later layout shift or the user's own scrolling.
   */
  useLayoutEffect(() => {
    if (anchor === null) {
      // Screen-level step: nothing to measure, so it is ready immediately.
      revealedOnce.current = true;
      return;
    }
    if (!anchor) {
      onClose();
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a new step restarts the settle→scroll→reveal cycle
    setReady(revealedOnce.current);
    let cancelled = false;
    let timer = 0;
    // TWO deadlines, because "not ready yet" and "not here at all" are
    // different problems. An element that EXISTS but measures 0x0 is mid-mount
    // or mid-fetch and deserves the long wait. An element that is not in the
    // DOM is usually conditional markup this store does not render — the wallet
    // plan notice on a per-order store, say — and waiting 8 seconds to admit
    // that reads as a dead "Next" button. Skip those quickly.
    const presentDeadline = Date.now() + 8000;
    const absentDeadline = Date.now() + 1500;
    let settled = false;
    let lastKey = "";
    let stableReads = 0;
    let revealedAt = 0;
    let scrollTries = 0;

    const commit = (next: Rect) => {
      // Same-value guard: a scroll listener firing on every frame would
      // otherwise re-render continuously.
      setRect((prev) =>
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.height === next.height
          ? prev
          : next,
      );
    };

    const track = () => {
      if (cancelled || !settled) return;
      const next = readRect(anchor);
      if (!next) return;
      commit(next);
      // A shift in the first moment after the reveal is the page finishing, not
      // the seller scrolling — correct for it. After that window the spotlight
      // follows the anchor and never moves the page, so it cannot fight them.
      const drifted =
        next.top < 0 || next.top + next.height > window.innerHeight;
      if (drifted && Date.now() - revealedAt < 1200 && scrollTries < 3) {
        scrollTries += 1;
        document
          .querySelector<HTMLElement>(`[data-tour="${anchor}"]`)
          ?.scrollIntoView({ block: "center" });
      }
    };

    /**
     * Re-checks visibility right before showing. The off-screen test in
     * `settle` can be answered correctly and then go stale — content below the
     * anchor finishes laying out and pushes it past the fold — so a single
     * check let a step reveal just under the edge of the viewport. Bounded, so
     * an anchor that cannot be brought into view (taller than the viewport,
     * inside a container that will not scroll) reveals where it is instead of
     * looping.
     */
    const reveal = () => {
      if (cancelled) return;
      const next = readRect(anchor);
      if (!next) return;
      const stillOffScreen =
        next.top < 0 || next.top + next.height > window.innerHeight;
      if (stillOffScreen && scrollTries < 3) {
        scrollTries += 1;
        document
          .querySelector<HTMLElement>(`[data-tour="${anchor}"]`)
          ?.scrollIntoView({ block: "center" });
        timer = window.setTimeout(reveal, 80);
        return;
      }
      settled = true;
      revealedAt = Date.now();
      commit(next);
      revealedOnce.current = true;
      setReady(true);
    };

    const settle = () => {
      if (cancelled) return;
      const next = readRect(anchor);
      if (next) {
        const key = `${next.top},${next.left},${next.width},${next.height}`;
        if (key !== lastKey) {
          lastKey = key;
          stableReads = 1;
          timer = window.setTimeout(settle, 60);
          return;
        }
        stableReads += 1;
        // Three identical readings (~180ms), not two: a settings panel reaches
        // its own final size while the cards BELOW it are still mounting, and
        // then everything shifts down. Two reads cleared that bar; three does
        // not, and the cost is one extra tick nobody perceives.
        if (stableReads < 3) {
          timer = window.setTimeout(settle, 60);
          return;
        }
        // Scrolling is INSTANT, not smooth (see `reveal`): a smooth scroll
        // finishes whenever it finishes — longer for a longer distance, and not
        // at all where the compositor is throttled — so any fixed wait after it
        // either reveals mid-scroll or stalls for no reason. Jumping the page
        // and THEN fading the highlight in is the steadier of the two to watch.
        reveal();
        return;
      }
      const inDom = document.querySelector(`[data-tour="${anchor}"]`) !== null;
      const deadline = inDom ? presentDeadline : absentDeadline;
      if (Date.now() < deadline) {
        timer = window.setTimeout(settle, 60);
        return;
      }
      // The OPENING step is never skipped for a missing anchor. It answers
      // "what is this screen?", and it anchors `page-main`, which the
      // full-screen editor has no equivalent of — skipping it there opened the
      // tour on step 2, mid-explanation. With no rect it presents screen-level:
      // plain dim, centred card, which is what that step wanted anyway.
      if (index === 0) {
        settled = true;
        setRect(null);
        revealedOnce.current = true;
        setReady(true);
        return;
      }
      // Genuinely absent — move past it rather than spotlighting nothing.
      setIndex((i) => i + 1);
    };

    settle();
    /**
     * Drop the PREVIOUS step's cut-out if this one is slow to appear.
     *
     * `rect` is step state that outlives the step: until the new anchor is
     * measured the overlay keeps painting the old hole, so moving from the
     * customers list to the profile it had just opened left the list spotlit
     * for as long as the drawer took to mount — the tour pointing at the thing
     * it had finished talking about. Clearing it presents the step like a
     * screen-level one (plain dim, centred card) until the real rect lands.
     *
     * After a GRACE, not immediately: an anchor that is already on the page
     * settles in ~180ms, and blanking the cut-out for that long only to put it
     * straight back is its own flicker. Only a wait long enough to notice is
     * worth correcting.
     */
    const staleTimer = window.setTimeout(() => {
      if (!cancelled && !settled) setRect(null);
    }, 250);
    const poll = window.setInterval(track, 250);
    window.addEventListener("resize", track);
    window.addEventListener("scroll", track, true);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.clearTimeout(staleTimer);
      window.clearInterval(poll);
      window.removeEventListener("resize", track);
      window.removeEventListener("scroll", track, true);
    };
  }, [anchor, index, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Pointing at a button and leaving the card up after it is pressed would
  // dim the very surface the press just opened. Capture, so it fires before
  // the button's own handler navigates or opens anything.
  useEffect(() => {
    if (!clickThrough || !anchor) return;
    const waitingForClick = step?.awaitClick === true;
    const lastStep = index >= steps.length - 1;
    const onDocClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const inAnchor = target?.closest(`[data-tour="${anchor}"]`);
      if (!inAnchor) return;
      // When the page nominates items, only an item counts: a tap on the list's
      // padding opens no profile, so it must not advance to the step that
      // explains one.
      if (
        waitingForClick &&
        inAnchor.querySelector("[data-tour-item]") &&
        !target?.closest("[data-tour-item]")
      ) {
        return;
      }
      // The seller did the thing. Move on to the step explaining what they just
      // opened — or, when this was the last step, get out of their way: on a
      // phone the tap opens the profile full-screen and there is nothing left
      // to say over it.
      if (!waitingForClick || lastStep) (onProceed ?? onClose)();
      else setIndex((i) => i + 1);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [
    anchor,
    clickThrough,
    index,
    onClose,
    onProceed,
    step?.awaitClick,
    steps.length,
  ]);

  if (!step || (!ready && anchor !== null)) return null;
  /**
   * Screen-level presentation — plain dim, centred card, no cut-out — for three
   * cases:
   *  - an explicitly anchorless step;
   *  - a step whose anchor has not been measured yet (rendering nothing
   *    instead blanked the whole tour on "Next");
   *  - an anchor that FILLS the viewport. The cut-out dims everything OUTSIDE
   *    itself, so spotlighting the whole content area dims nothing: on a phone
   *    the intro step lit the entire screen and read as no highlight at all.
   *    Past ~85% of the viewport there is nothing left to contrast against, so
   *    dim the lot and centre the card instead.
   */
  const fillsViewport =
    rect !== null &&
    rect.width * rect.height >=
      window.innerWidth * window.innerHeight * 0.85;
  const screenLevel = step.anchor === null || rect === null || fillsViewport;

  const isLast = index === steps.length - 1;
  const awaitingClick = step.awaitClick === true;
  // Over the first nominated item, else the top of the anchor — read at render
  // because the hand only has to be right once the highlight has settled, and
  // every settle already re-renders this component.
  const handTarget = awaitingClick && anchor ? readItemRect(anchor) : null;
  const skipIsRedundant = isLast && !onProceed && !awaitingClick;
  // Below the anchor when there is room, above it when there is room there, and
  // otherwise INSIDE it at the top. Never left/right: that would need mirroring
  // for RTL and breaks on a 375px viewport.
  //
  // The third case is not a fallback nobody hits — spotlighting a whole drawer
  // or modal leaves no room on either side, and the old maths pushed the card
  // clean off the top of the screen. Top-of-anchor covers that surface's header
  // rather than its Save button.
  // WHAT THE CARD IS PLACED AGAINST. Normally the anchor — but a step that
  // waits for a tap points a hand at one row inside a full-height list, and
  // placing the card against the list put it straight over that row: the seller
  // was told to tap something the instruction was covering. Placed against the
  // row instead, the card sits just below the hand. The cut-out still frames
  // the whole list, which is what the words describe.
  const placeRect = awaitingClick && handTarget ? handTarget : rect;
  const CARD_H = 170;
  const roomBelow =
    placeRect !== null && window.innerHeight - (placeRect.top + placeRect.height) > CARD_H + GAP;
  const roomAbove = placeRect !== null && placeRect.top > CARD_H + GAP;
  /**
   * A tall anchor — a full-height side rail, a drawer — has no room above or
   * below, and putting the card INSIDE it covers the very thing being
   * explained. When such an anchor leaves enough clear width to one side, the
   * card goes there instead, vertically centred.
   *
   * This is computed from measured pixels, not `left`/`right` utilities, so it
   * is corplaceRect in RTL without mirroring anything. It only engages when the
   * space genuinely exists, which on a 375px phone it never does — there the
   * inside-at-top fallback still applies.
   */
  const spaceStart = placeRect === null ? 0 : placeRect.left;
  const spaceEnd =
    placeRect === null ? 0 : window.innerWidth - (placeRect.left + placeRect.width);
  // Demand a clear gutter, not a tight fit. The anchor's placeRect can shift by a
  // few pixels after placement (a notice line appearing, a scrollbar arriving),
  // and a card sized to exactly fit then clips the very edge of the thing it is
  // explaining. The extra GAP absorbs that; the clamps below make the overlap
  // impossible rather than unlikely.
  const needed = CARD_W + GAP * 3;
  const besideLeft =
    placeRect !== null && !roomBelow && !roomAbove && spaceStart >= needed
      ? Math.max(GAP, Math.min(placeRect.left - GAP - CARD_W, window.innerWidth - CARD_W - GAP))
      : placeRect !== null && !roomBelow && !roomAbove && spaceEnd >= needed
        ? Math.max(
            placeRect.left + placeRect.width + GAP,
            Math.min(placeRect.left + placeRect.width + GAP, window.innerWidth - CARD_W - GAP),
          )
        : null;

  const cardTop =
    placeRect === null
      ? undefined
      : besideLeft !== null
        ? Math.max(GAP, placeRect.top + placeRect.height / 2 - CARD_H / 2)
        : roomBelow
          ? placeRect.top + placeRect.height + GAP
          : roomAbove
            ? undefined
            : Math.max(GAP, placeRect.top + GAP);
  const cardBottom =
    placeRect !== null && besideLeft === null && !roomBelow && roomAbove
      ? Math.max(GAP, window.innerHeight - placeRect.top + GAP)
      : undefined;
  // Clamp horizontally so the card never leaves the viewport in either
  // direction — the same maths serves LTR and RTL.
  const cardLeft =
    placeRect === null
      ? Math.max(GAP, (window.innerWidth - CARD_W) / 2)
      : besideLeft !== null
        ? besideLeft
        : Math.min(
            Math.max(GAP, placeRect.left + placeRect.width / 2 - CARD_W / 2),
            Math.max(GAP, window.innerWidth - CARD_W - GAP),
          );

  return createPortal(
    <div
      // ABOVE every surface the tour can point at, not just the page beneath
      // it. At z-60 the customers step that explains the profile was rendered
      // under the mobile full-screen (z-70) the previous step had just opened:
      // correct state, invisible tour. The full screens sit at 70 and their own
      // menus at 80, so 90 clears both.
      className={`coachmark-in fixed inset-0 z-[90] ${
        clickThrough ? "pointer-events-none" : ""
      }`}
      role="dialog"
      aria-modal={!clickThrough}
    >
      {/* Only a step with NOTHING to point at dims flat. A step whose anchor
          happens to fill the screen still gets the ring below — the ring is
          what marks the surface, and without it a full-screen drawer read as
          "the tour highlighted nothing". */}
      {rect === null ? (
        <div
          className="pointer-events-auto absolute inset-0 bg-[rgba(10,12,20,0.62)]"
          onClick={onClose}
        />
      ) : null}
      {/* The scrim is one element with a cut-out ring, rather than four boxes
          around the anchor: a box-shadow spread that large dims everything
          outside the hole and stays exact while the anchor moves. */}
      {rect ? (
      <div
        // Names the element being spotlighted — the one bit of state that is
        // otherwise invisible when a step points at the wrong thing.
        data-tour-active={anchor}
        // NO position transition. `transition-all` animated top/left/width
        // between rects, so every time the box was re-measured — the drift
        // correction after reveal, or moving to the next step — the highlight
        // visibly SLID into place, reading as "it came up from below the
        // section". It is only ever rendered once its anchor has settled, so
        // there is nothing to ease from: it belongs where it lands.
        className={`absolute rounded-[12px] ${
          clickThrough ? "pointer-events-none" : "pointer-events-auto"
        }`}
        onClick={clickThrough ? undefined : onClose}
        style={{
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
          boxShadow: "0 0 0 9999px rgba(10,12,20,0.62)",
          // Gold ring around the cut-out, matching the guided order tour's
          // spotlight. The dim alone says "look here somewhere"; the ring says
          // "this, exactly" — and it is the same mark in both tours, so the
          // seller learns it once.
          outline: "2px solid var(--gold-500)",
          outlineOffset: 2,
        }}
      />
      ) : null}
      {handTarget ?? (awaitingClick ? rect : null) ? (
        <TapHand rect={(handTarget ?? rect)!} />
      ) : null}
      <div
        className="pointer-events-auto absolute flex flex-col gap-2 rounded-[14px] bg-[color:var(--surface)] p-4 shadow-2xl"
        style={{
          // A screen-level card centres itself; an anchored one sits by its
          // anchor (see the placement rules above).
          // A step explaining a whole SURFACE — the product screen, the
          // customer profile — docks at the top. Centred, the card lands
          // squarely on the thing it is describing; pinned to the top it leaves
          // the surface visible underneath, which is the point of explaining
          // it. Only that case: a step with nothing to point at has no content
          // to keep clear, and a centred card is the calmer place for it.
          top: fillsViewport ? SCREEN_CARD_TOP : screenLevel ? "50%" : cardTop,
          bottom: cardBottom,
          left: cardLeft,
          transform:
            screenLevel && !fillsViewport ? "translateY(-50%)" : undefined,
          width: CARD_W,
          maxWidth: "calc(100vw - 24px)",
        }}
      >
        {/* Badge + counter, matching the guided order tour's card: a seller
            meets both, and "this is a walkthrough, not the app talking to you"
            should look the same in each. The badge also gives the card an
            identity when the progress line is hidden on a one-step tour. */}
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.06em] text-[color:var(--gold-600,var(--gold-500))]">
            <SparkIcon />
            {t(K.badge)}
          </span>
          {steps.length > 1 ? (
            <span className="text-[11px] font-bold text-[color:var(--text-muted)]">
              {t(K.progress, { current: index + 1, total: steps.length })}
            </span>
          ) : null}
        </div>
        <span className="text-[15px] font-bold text-[color:var(--text-primary)]">
          {step.title}
        </span>
        <span className="text-[13px] leading-relaxed text-[color:var(--text-secondary)]">
          {step.body}
        </span>
        {/* Said HERE, not beside the rows. The samples only ever exist while
            this card is up, so a notice on the page and a notice in the card
            are the same sentence twice in one screenful — and the card is where
            the seller is already reading. */}
        {sampleNotice ? (
          <span className="text-[12px] leading-relaxed text-[color:var(--text-muted)]">
            {sampleNotice}
          </span>
        ) : null}
        {/* "Skip" earns its place only while it does something the primary
            button does not: abandoning a tour that has steps left, or declining
            an action the primary would perform (`onProceed`). On the last step
            of a tour that only closes, the two buttons are the same button
            twice — asking the seller to choose between "Skip" and "Done" for an
            identical outcome. */}
        <div
          className={`mt-1 flex items-center gap-2 ${
            skipIsRedundant ? "justify-end" : "justify-between"
          }`}
        >
          {skipIsRedundant ? null : (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-2 py-1.5 text-[12px] font-semibold text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-alt)]"
            >
              {t(K.skip)}
            </button>
          )}
          {awaitingClick ? null : (
            <button
              type="button"
              onClick={() =>
                isLast ? (onProceed ?? onClose)() : setIndex((i) => i + 1)
              }
              className="rounded-lg bg-[color:var(--primary-500)] px-3 py-1.5 text-[12.5px] font-bold text-[color:var(--on-accent)]"
            >
              {isLast ? (proceedLabel ?? t(K.done)) : t(K.next)}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** The badge glyph — a small spark, the same idea as the tour's own copy. */
function SparkIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2.5 13.9 9l6.6 1.9-6.6 1.9L12 19.4 10.1 12.8 3.5 10.9 10.1 9z" />
    </svg>
  );
}
